import React from 'react';

import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import * as SavedSearchesActions from './SavedSearchesActions';
import SavedSearchSettingsModal, {SavedSearchSettingsModalModifiableFields} from './SavedSearchSettingsModal';
import {SavedSearch} from './SavedSearchUtil';

export interface ShareSearchButtonProps {
  searchSection: SearchSection;
  onSaved: (savedSearch: SavedSearch) => void;
  onDeleted: () => void;
  activeSavedSearch: SavedSearch;
}

export default function ShareSearchButton({
  searchSection,
  onSaved,
  onDeleted,
  activeSavedSearch,
}: ShareSearchButtonProps): JSX.Element | null {
  const onDelete = async (): Promise<boolean> => {
    const deleted = await SavedSearchesActions.confirmAndDelete(activeSavedSearch, searchSection);
    if (deleted) {
      onDeleted();
    }
    return deleted;
  };

  const onSave = async (values: SavedSearchSettingsModalModifiableFields): Promise<void> => {
    const updatedSavedSearch = await SavedSearchesActions.update(activeSavedSearch.id, {
      name: values.name,
      description: values.description,
      ownerJson: values.ownerJson,
      ownerInfoJson: values.ownerInfoJson,
      collaboratorsJson: {collaborators: values.collaboratorsJson},
    });
    onSaved(updatedSavedSearch);
  };

  return (
    <Button
      size={ButtonSize.SMALL}
      buttonStyle={ButtonStyle.SECONDARY}
      icon="share-v2"
      text="Share"
      onClick={async () => {
        await runModal(SavedSearchSettingsModal, {
          title: 'Saved Search Settings',
          onDelete: activeSavedSearch.permissions.admin ? onDelete : undefined,
          onRequestSubmit: onSave,
          savedSearchId: activeSavedSearch.id,
          initialValues: {
            name: activeSavedSearch.name,
            description: activeSavedSearch.description,
          },
          autoFocus: 'share',
          noWriteAccess: !activeSavedSearch.permissions.writable,
        });
      }}
      dataTestAttrs={{component: 'ShareSearchButton', element: 'shareButton'}}
    />
  );
}
